import React, { useState, useEffect } from 'react';
import { TAXONOMY, TEAM_LISTS } from '../lib/taxonomy';

const TOP_LEVEL = Object.keys(TAXONOMY);

function getNodeAt(category, path) {
  let node = TAXONOMY[category];
  for (const seg of path) {
    if (!node || typeof node !== 'object' || Array.isArray(node)) return undefined;
    node = node[seg];
  }
  return node;
}

export default function InterestPickerModal({ isOpen, onClose, initialInterests, onSave }) {
  const [leftSel, setLeftSel]     = useState(TOP_LEVEL[0]);
  const [navPath, setNavPath]     = useState([]);
  const [rightFocus, setRightFocus] = useState(null);
  const [selected, setSelected]   = useState(new Set());

  useEffect(() => {
    if (isOpen) {
      setSelected(new Set(initialInterests || []));
      setLeftSel(TOP_LEVEL[0]);
      setNavPath([]);
      setRightFocus(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const currentNode = getNodeAt(leftSel, navPath);
  const centerItems = (currentNode && !Array.isArray(currentNode))
    ? Object.keys(currentNode).filter(k => k !== 'Other')
    : [];
  const currentPathStr = [leftSel, ...navPath].join(' › ');

  // Right panel content
  const rightNode = rightFocus ? getNodeAt(leftSel, [...navPath, rightFocus]) : null;
  const teamList  = rightFocus && TEAM_LISTS[rightFocus] ? TEAM_LISTS[rightFocus] : null;
  const leafChips = !teamList && Array.isArray(rightNode)
    ? rightNode.filter(i => i !== 'Specific Team' && i !== 'Other')
    : null;
  const rightPathStr = rightFocus ? [leftSel, ...navPath, rightFocus].join(' › ') : null;

  function toggleInterest(path) {
    const next = new Set(selected);
    if (next.has(path)) next.delete(path); else next.add(path);
    setSelected(next);
  }

  function handleCenterItemClick(key) {
    const childNode = currentNode?.[key];
    const isLeaf = !childNode || Array.isArray(childNode)
      || (typeof childNode === 'object' && Object.keys(childNode).length === 0);
    if (!isLeaf) {
      setNavPath([...navPath, key]);
      setRightFocus(null);
    } else {
      setRightFocus(key);
    }
  }

  function handleBreadcrumb(idx) {
    setNavPath(navPath.slice(0, idx));
    setRightFocus(null);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
    }}>
      <div style={{
        background: '#fff', borderRadius: '12px',
        width: '100%', maxWidth: '880px', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '1rem 1.25rem', borderBottom: '1px solid #e8e8e8',
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#111' }}>My Interests</div>
            <div style={{ fontSize: '0.76rem', color: '#888', marginTop: '0.15rem' }}>
              Pick topics and teams to personalize your feed
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.38rem 0.85rem', borderRadius: '6px',
                border: '1px solid #ccc', background: '#fff',
                color: '#555', fontSize: '0.82rem', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => onSave([...selected])}
              style={{
                padding: '0.38rem 0.85rem', borderRadius: '6px',
                border: 'none', background: '#3366cc',
                color: '#fff', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
              }}
            >
              Save{selected.size > 0 ? ` (${selected.size})` : ''}
            </button>
          </div>
        </div>

        {/* 3-panel body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* LEFT: category list */}
          <div style={{
            width: '175px', flexShrink: 0,
            borderRight: '1px solid #e8e8e8',
            overflowY: 'auto',
            padding: '0.4rem 0',
          }}>
            {TOP_LEVEL.map(cat => (
              <div
                key={cat}
                onClick={() => { setLeftSel(cat); setNavPath([]); setRightFocus(null); }}
                style={{
                  padding: '0.52rem 1rem', fontSize: '0.81rem', cursor: 'pointer',
                  fontWeight: leftSel === cat ? 700 : 400,
                  color: leftSel === cat ? '#3366cc' : '#333',
                  background: leftSel === cat ? '#eef3ff' : 'transparent',
                  borderLeft: `3px solid ${leftSel === cat ? '#3366cc' : 'transparent'}`,
                  lineHeight: 1.35,
                }}
              >
                {cat}
              </div>
            ))}
          </div>

          {/* CENTER: sub-nav */}
          <div style={{
            width: '275px', flexShrink: 0,
            borderRight: '1px solid #e8e8e8',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Breadcrumb */}
            <div style={{
              padding: '0.5rem 0.85rem', borderBottom: '1px solid #f0f0f0',
              fontSize: '0.72rem', color: '#888', flexShrink: 0,
              display: 'flex', flexWrap: 'wrap', gap: '0.15rem', alignItems: 'center',
              minHeight: '32px',
            }}>
              <span
                onClick={() => handleBreadcrumb(0)}
                style={{ cursor: 'pointer', color: navPath.length > 0 ? '#3366cc' : '#666' }}
              >
                {leftSel}
              </span>
              {navPath.map((seg, idx) => (
                <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
                  <span style={{ color: '#bbb' }}> › </span>
                  <span
                    onClick={() => handleBreadcrumb(idx + 1)}
                    style={{
                      cursor: idx < navPath.length - 1 ? 'pointer' : 'default',
                      color: idx < navPath.length - 1 ? '#3366cc' : '#666',
                    }}
                  >
                    {seg}
                  </span>
                </span>
              ))}
            </div>

            {/* Items */}
            <div style={{ overflowY: 'auto', flex: 1 }}>
              {/* Follow-all toggle for current path */}
              {navPath.length > 0 && (
                <div
                  onClick={() => toggleInterest(currentPathStr)}
                  style={{
                    padding: '0.52rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer',
                    borderBottom: '1px solid #ececec',
                    color: selected.has(currentPathStr) ? '#fff' : '#3366cc',
                    background: selected.has(currentPathStr) ? '#3366cc' : '#f0f5ff',
                    fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '0.3rem',
                  }}
                >
                  <span>{selected.has(currentPathStr) ? '✓' : '+'}</span>
                  <span>Follow all: {navPath[navPath.length - 1]}</span>
                </div>
              )}

              {centerItems.map(key => {
                const childNode = currentNode?.[key];
                const hasChildren = childNode && typeof childNode === 'object'
                  && !Array.isArray(childNode) && Object.keys(childNode).length > 0;
                const childPath = `${currentPathStr} › ${key}`;
                const isSel = selected.has(childPath);
                const isFocused = rightFocus === key;

                return (
                  <div
                    key={key}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.52rem 0.85rem', fontSize: '0.81rem',
                      borderBottom: '1px solid #f5f5f5',
                      background: isFocused ? '#f5f8ff' : isSel ? '#f0f7ee' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { if (!isFocused && !isSel) e.currentTarget.style.background = '#fafafa'; }}
                    onMouseLeave={e => { if (!isFocused && !isSel) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span
                      onClick={() => handleCenterItemClick(key)}
                      style={{ flex: 1, color: '#333', lineHeight: 1.3 }}
                    >
                      {key}
                    </span>
                    <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', marginLeft: '0.4rem' }}>
                      <span
                        onClick={e => { e.stopPropagation(); toggleInterest(childPath); }}
                        style={{
                          fontSize: '0.68rem', padding: '0.1rem 0.38rem',
                          borderRadius: '4px', cursor: 'pointer', lineHeight: '1.5',
                          border: `1px solid ${isSel ? '#4a8f3f' : '#ccc'}`,
                          background: isSel ? '#4a8f3f' : 'transparent',
                          color: isSel ? '#fff' : '#888',
                        }}
                      >
                        {isSel ? '✓' : '+'}
                      </span>
                      {(hasChildren || TEAM_LISTS[key]) && (
                        <span
                          onClick={() => handleCenterItemClick(key)}
                          style={{ color: '#bbb', fontSize: '0.9rem', lineHeight: 1 }}
                        >
                          ›
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {centerItems.length === 0 && (
                <div style={{ padding: '1rem 0.85rem', fontSize: '0.78rem', color: '#bbb' }}>
                  No sub-categories
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: teams or leaf chips + selected summary */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0.85rem' }}>

              {teamList && (
                <>
                  <div style={{ fontSize: '0.73rem', color: '#888', fontWeight: 700, marginBottom: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {rightFocus} Teams
                  </div>
                  <div
                    onClick={() => toggleInterest(rightPathStr)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.28rem 0.65rem', borderRadius: '16px',
                      fontSize: '0.73rem', cursor: 'pointer', marginBottom: '0.6rem',
                      border: `1px solid ${selected.has(rightPathStr) ? '#3366cc' : '#ddd'}`,
                      background: selected.has(rightPathStr) ? '#3366cc' : '#f5f5f5',
                      color: selected.has(rightPathStr) ? '#fff' : '#555',
                      fontWeight: 600,
                    }}
                  >
                    {selected.has(rightPathStr) ? '✓' : '+'} Follow all {rightFocus}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.38rem' }}>
                    {teamList.map(team => {
                      const teamPath = `${rightPathStr} › ${team}`;
                      const isSel = selected.has(teamPath);
                      return (
                        <div
                          key={team}
                          onClick={() => toggleInterest(teamPath)}
                          style={{
                            padding: '0.3rem 0.65rem', borderRadius: '16px',
                            fontSize: '0.76rem', cursor: 'pointer',
                            border: `1px solid ${isSel ? '#3366cc' : '#ddd'}`,
                            background: isSel ? '#3366cc' : '#f8f8f8',
                            color: isSel ? '#fff' : '#333',
                            fontWeight: isSel ? 600 : 400,
                          }}
                        >
                          {isSel ? '✓ ' : ''}{team}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {leafChips && leafChips.length > 0 && (
                <>
                  <div style={{ fontSize: '0.73rem', color: '#888', fontWeight: 700, marginBottom: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {rightFocus}
                  </div>
                  <div
                    onClick={() => toggleInterest(rightPathStr)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                      padding: '0.28rem 0.65rem', borderRadius: '16px',
                      fontSize: '0.73rem', cursor: 'pointer', marginBottom: '0.6rem',
                      border: `1px solid ${selected.has(rightPathStr) ? '#3366cc' : '#ddd'}`,
                      background: selected.has(rightPathStr) ? '#3366cc' : '#f5f5f5',
                      color: selected.has(rightPathStr) ? '#fff' : '#555',
                      fontWeight: 600,
                    }}
                  >
                    {selected.has(rightPathStr) ? '✓' : '+'} Follow all {rightFocus}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.38rem' }}>
                    {leafChips.map(chip => {
                      const chipPath = `${rightPathStr} › ${chip}`;
                      const isSel = selected.has(chipPath);
                      return (
                        <div
                          key={chip}
                          onClick={() => toggleInterest(chipPath)}
                          style={{
                            padding: '0.3rem 0.65rem', borderRadius: '16px',
                            fontSize: '0.76rem', cursor: 'pointer',
                            border: `1px solid ${isSel ? '#3366cc' : '#ddd'}`,
                            background: isSel ? '#eef3ff' : '#f8f8f8',
                            color: isSel ? '#3366cc' : '#333',
                            fontWeight: isSel ? 600 : 400,
                          }}
                        >
                          {isSel ? '✓ ' : ''}{chip}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {!teamList && !leafChips && (
                <div style={{ color: '#bbb', fontSize: '0.82rem', paddingTop: '1.5rem', textAlign: 'center' }}>
                  {rightFocus
                    ? `Select a team or follow all ${rightFocus}`
                    : 'Click a topic or league on the left to explore'}
                </div>
              )}
            </div>

            {/* Selected interests summary */}
            {selected.size > 0 && (
              <div style={{
                borderTop: '1px solid #ececec',
                padding: '0.6rem 0.85rem',
                flexShrink: 0, maxHeight: '130px', overflowY: 'auto',
              }}>
                <div style={{ fontSize: '0.7rem', color: '#888', fontWeight: 700, marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Selected ({selected.size})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {[...selected].map(interest => (
                    <div
                      key={interest}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.25rem',
                        padding: '0.18rem 0.5rem', borderRadius: '10px',
                        background: '#eef3ff', border: '1px solid #c5d5f5',
                        fontSize: '0.7rem', color: '#3366cc',
                        maxWidth: '200px',
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {interest.split(' › ').slice(-2).join(' › ')}
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        aria-label={`Remove ${interest.split(' › ').slice(-1)[0]}`}
                        onClick={() => toggleInterest(interest)}
                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && toggleInterest(interest)}
                        style={{ cursor: 'pointer', color: '#99aacc', fontWeight: 700, flexShrink: 0 }}
                      >
                        ×
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
